import { useState } from 'react'
import { ExternalLink } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { ContentLocale } from '@/data/public-catalog'
import { preanalyticsProducts, type PreanalyticsProductKey } from '@/data/product-pages/preanalytics'
import { ProductMedia } from './ProductMedia'

type BodyProps = { language: ContentLocale }

function useProductText(productKey: PreanalyticsProductKey, language: ContentLocale) {
  const { t } = useTranslation('common')
  const sourceLanguage = productKey === 'eugenio' ? language : 'it'
  return (key: string, options?: Record<string, unknown>) => t(`productPage.${productKey}.${key}`, { lng: sourceLanguage, ...options })
}

function EugenioBody({ language }: BodyProps) {
  const text = useProductText('eugenio', language)
  const strengths = text('strengths', { returnObjects: true }) as unknown as Array<{ title: string; description: string }>
  const workflow = text('workflow', { returnObjects: true }) as unknown as string[]
  const gallery = preanalyticsProducts['prd-eugenio'].gallery
  const [selected, setSelected] = useState(0)
  return <>
    <section className="shell-container product-section preanalytics-body eugenio-body" aria-labelledby="eugenio-body-title">
      <div className="eugenio-intro product-copy">
        <h2 id="eugenio-body-title">{text('bodyTitle')}</h2>
        <p>{text('monitoring')}</p>
        <p>{text('integration')}</p>
      </div>
      <section className="eugenio-strengths" aria-labelledby="eugenio-strengths-title">
        <h2 id="eugenio-strengths-title">{text('strengthsTitle')}</h2>
        <dl>{strengths.map(item => <div key={item.title}><dt>{item.title}</dt><dd>{item.description}</dd></div>)}</dl>
      </section>
      <section className="eugenio-workflow" aria-labelledby="eugenio-workflow-title">
        <h2 id="eugenio-workflow-title">{text('workflowTitle')}</h2>
        <ol>{workflow.map(step => <li key={step}>{step}</li>)}</ol>
      </section>
    </section>
    <section className="product-section eugenio-gallery" aria-labelledby="eugenio-gallery-title">
      <div className="shell-container">
        <h2 id="eugenio-gallery-title">{text('galleryTitle')}</h2>
        <ProductMedia path={gallery[selected]} altKey={`productPage.eugenio.galleryAlt${selected + 1}`} className="eugenio-gallery-main" />
        <div className="eugenio-thumbnails" role="group" aria-label={text('galleryLabel')}>
          {gallery.map((path, index) => <button key={path} type="button" aria-pressed={selected === index} aria-label={text('galleryChoice', { index: index + 1 })} onClick={() => setSelected(index)}>
            <ProductMedia path={path} altKey={`productPage.eugenio.galleryAlt${index + 1}`} />
          </button>)}
        </div>
        <p className="product-video-reference"><a href="https://www.youtube.com/watch?v=_S06V02eTxI" target="_blank" rel="noreferrer">{text('videoLink')}<ExternalLink aria-hidden="true" size={18} /></a></p>
      </div>
    </section>
  </>
}

function FlashbotBody({ language }: BodyProps) {
  const text = useProductText('flashbot', language)
  const paragraphs = text('paragraphs', { returnObjects: true }) as unknown as string[]
  return <section className="shell-container product-section preanalytics-body flashbot-body" aria-labelledby="flashbot-body-title">
    <div lang={language === 'en' ? 'it' : undefined}>
      <h2 id="flashbot-body-title">{text('bodyTitle')}</h2>
      <div className="flashbot-sequence">{paragraphs.map((paragraph, index) => <p key={index}>{paragraph}</p>)}</div>
    </div>
  </section>
}

function TubeRackBody({ language }: BodyProps) {
  const text = useProductText('tubeRack', language)
  const paragraphs = text('paragraphs', { returnObjects: true }) as unknown as string[]
  const variants = text('variants', { returnObjects: true }) as unknown as Array<{ title: string; details: string[] }>
  const media = [preanalyticsProducts['prd-portaprovette'].primaryMedia, preanalyticsProducts['prd-portaprovette'].secondaryMedia]
  return <section className="shell-container product-section preanalytics-body rack-body" aria-labelledby="rack-body-title">
    <div className="rack-copy product-copy" lang={language === 'en' ? 'it' : undefined}>
      <h2 id="rack-body-title">{text('bodyTitle')}</h2>
      {paragraphs.map((paragraph, index) => <p key={index}>{paragraph}</p>)}
    </div>
    <div className="rack-variants" lang={language === 'en' ? 'it' : undefined}>
      {variants.map((variant, index) => <article key={variant.title}>
        <ProductMedia path={media[index]} altKey={`productPage.tubeRack.variantAlt${index + 1}`} />
        <div><h3>{variant.title}</h3><ul>{variant.details.map(detail => <li key={detail}>{detail}</li>)}</ul></div>
      </article>)}
    </div>
  </section>
}

function TiposBody({ language }: BodyProps) {
  const text = useProductText('tipos', language)
  const stages = text('stages', { returnObjects: true }) as unknown as Array<{ title: string; description: string }>
  return <section className="shell-container product-section preanalytics-body tipos-body" aria-labelledby="tipos-body-title">
    <div lang={language === 'en' ? 'it' : undefined}>
      <h2 id="tipos-body-title">{text('bodyTitle')}</h2>
      <div className="tipos-stages">{stages.map(stage => <section key={stage.title}><h3>{stage.title}</h3><p>{stage.description}</p></section>)}</div>
    </div>
  </section>
}

function SorterBody({ productKey, language }: BodyProps & { productKey: 'tt2000' | 'tt2000Iq' }) {
  const text = useProductText(productKey, language)
  const paragraphs = text('paragraphs', { returnObjects: true }) as unknown as string[]
  const specs = text('specs', { returnObjects: true }) as unknown as Array<{ value: string; label: string }>
  return <section className={`shell-container product-section preanalytics-body sorter-body sorter-body--${productKey}`} aria-labelledby={`${productKey}-body-title`}>
    <div lang={language === 'en' ? 'it' : undefined}>
      <div className="sorter-copy product-copy"><h2 id={`${productKey}-body-title`}>{text('bodyTitle')}</h2>{paragraphs.map((paragraph, index) => <p key={index}>{paragraph}</p>)}</div>
      {productKey === 'tt2000Iq' && <div className="iq-verification" aria-label={text('verificationLabel')}><span>{text('barcode')}</span><span>{text('camera')}</span><strong>{text('crossCheck')}</strong></div>}
      <dl className="sorter-specs">{specs.map(spec => <div key={spec.value}><dt>{spec.value}</dt><dd>{spec.label}</dd></div>)}</dl>
    </div>
  </section>
}

export function PreanalyticsBody({ productKey, language }: { productKey: PreanalyticsProductKey; language: ContentLocale }) {
  if (productKey === 'eugenio') return <EugenioBody language={language} />
  if (productKey === 'flashbot') return <FlashbotBody language={language} />
  if (productKey === 'tubeRack') return <TubeRackBody language={language} />
  if (productKey === 'tipos') return <TiposBody language={language} />
  return <SorterBody productKey={productKey} language={language} />
}
